import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChatCircleDots, Phone, X, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * GlobalAstrologerRing — mounts at App root so an astrologer hears the
 * "ring" no matter which page they're on (Reports, Astrologers list,
 * Settings, anywhere). Polls /astrologer/chats/incoming + /astrologer/
 * calls/incoming on a 3s cadence when:
 *
 *   1. The user is signed in as an astrologer (verified via /astrologer/me)
 *   2. They are NOT already on /astrologer/dashboard (where the page-local
 *      polling already shows a much richer chart-context modal)
 *
 * On Accept, the astrologer is taken straight to the dashboard with
 * ?fullscreen_chat={chat_id} / ?fullscreen_call={call_id} so the dashboard
 * auto-opens that conversation in the full-screen takeover view.
 *
 * Decline button on chat-incoming posts /decline immediately. For voice
 * calls there's no in-app decline (Twilio handles ring-timeout) so the
 * banner just dismisses.
 */
const POLL_MS = 3000;
const ME_POLL_MS = 60_000; // re-check astrologer identity every minute

export default function GlobalAstrologerRing() {
  const location = useLocation();
  const navigate = useNavigate();
  // The signed-in CUSTOMER (if any). Used to suppress self-rings: when a
  // browser holds BOTH a customer session AND a stale/QA astrologer session
  // (common during QA where the same person tests both sides), the global
  // ring would otherwise fire for chats the same person just initiated.
  const { user: currentCustomer } = useAuth();
  // null = unknown / not-an-astrologer, object = signed-in astrologer
  const [me, setMe] = useState(null);
  const [incomingChat, setIncomingChat] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [accepting, setAccepting] = useState(false);
  const beepRef = useRef(null);

  // Per-chat self-skip is the ONLY guard we apply (see the poll effects
  // below) — `c.customer_id !== myUid`. That covers the QA-cookie-share
  // case where the same user is signed in on both sides. An earlier
  // version had a global `selfCollision` hard-disable on email match,
  // but that incorrectly suppressed the banner for real astrologers who
  // legitimately have a customer account on the same email (which is
  // every astrologer testing on their own platform).

  // Dashboard page already runs its own (much richer) polling +
  // modal — skip ours on that exact path to avoid double-modals.
  const onDashboard = location.pathname === "/astrologer/dashboard";
  // Login / public auth pages — never poll
  const onAuthPage = location.pathname === "/astrologer/login" || location.pathname === "/auth";

  // ── Identify the signed-in astrologer (if any) ────────────────────────
  useEffect(() => {
    if (onAuthPage) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/astrologer/me");
        if (!cancelled) setMe(data || null);
      } catch (_) {
        if (!cancelled) setMe(null);
      }
    };
    tick();
    const id = setInterval(tick, ME_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [onAuthPage]);

  // ── Poll for ringing chats ─────────────────────────────────────────────
  useEffect(() => {
    if (!me || onDashboard || onAuthPage) {
      setIncomingChat(null);
      return undefined;
    }
    if (!me.chat_enabled) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/astrologer/chats/incoming");
        if (Array.isArray(data) && data.length > 0) {
          // Skip dismissed AND any chat the CURRENT customer initiated — the
          // global ring must NEVER pop on the seeker's own screen for a chat
          // they're starting themselves (covers cookie-sharing QA case).
          const myUid = currentCustomer?.user_id;
          const myEmail = (currentCustomer?.email || "").toLowerCase();
          const next = data.find((c) =>
            !dismissed.has(c.chat_id)
            && c.customer_id !== myUid
            && (c.customer_email || "").toLowerCase() !== myEmail
          ) || null;
          setIncomingChat((cur) => (cur && cur.chat_id === next?.chat_id ? cur : next));
        } else {
          setIncomingChat(null);
        }
      } catch (_) { /* swallow */ }
      if (!cancelled) setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [me, me?.chat_enabled, onDashboard, onAuthPage, dismissed, currentCustomer?.user_id, currentCustomer?.email]);

  // ── Poll for ringing voice calls ───────────────────────────────────────
  useEffect(() => {
    if (!me || onDashboard || onAuthPage) {
      setIncomingCall(null);
      return undefined;
    }
    if (!me.call_enabled) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/astrologer/calls/incoming");
        if (Array.isArray(data) && data.length > 0) {
          const myUid = currentCustomer?.user_id;
          const myEmail = (currentCustomer?.email || "").toLowerCase();
          const next = data.find((c) =>
            !dismissed.has(c.call_id)
            && c.seeker_user_id !== myUid
            && (c.seeker_email || "").toLowerCase() !== myEmail
          ) || null;
          setIncomingCall((cur) => (cur && cur.call_id === next?.call_id ? cur : next));
        } else {
          setIncomingCall(null);
        }
      } catch (_) { /* swallow */ }
      if (!cancelled) setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [me, me?.call_enabled, onDashboard, onAuthPage, dismissed, currentCustomer?.user_id, currentCustomer?.email]);

  // ── Audible ring while there's an incoming chat OR call ────────────────
  const hasRinger = !!(incomingChat || incomingCall);
  useEffect(() => {
    if (!hasRinger) {
      if (beepRef.current) { beepRef.current.stop(); beepRef.current = null; }
      return undefined;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;
    let stopped = false;
    let ctx;
    try { ctx = new Ctx(); } catch { return undefined; }
    const blip = () => {
      if (stopped) return;
      try {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        // Voice call = lower pitch, chat = higher (matches dashboard cues)
        o.frequency.value = incomingCall ? 660 : 880;
        g.gain.value = 0.0001;
        o.connect(g).connect(ctx.destination);
        const now = ctx.currentTime;
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(0.16, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        o.start(now);
        o.stop(now + 0.4);
      } catch { /* AudioContext suspended (no user-gesture yet) */ }
      setTimeout(blip, 1500);
    };
    beepRef.current = {
      stop: () => {
        stopped = true;
        try { ctx.close().catch(() => {}); } catch { /* ignore */ }
      },
    };
    blip();
    return () => {
      stopped = true;
      try { ctx.close().catch(() => {}); } catch { /* ignore */ }
    };
  }, [hasRinger, !!incomingCall]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const acceptChat = useCallback(async () => {
    if (!incomingChat || accepting) return;
    setAccepting(true);
    const chatId = incomingChat.chat_id;
    try {
      await api.post(`/astrologer/chats/${chatId}/accept`);
      setIncomingChat(null);
      toast.success(`Connected with ${incomingChat.customer_name}`);
      // Dashboard auto-opens the chat in full-screen takeover when it
      // sees the `fullscreen_chat` query param on mount.
      navigate(`/astrologer/dashboard?fullscreen_chat=${chatId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept chat.");
    } finally {
      setAccepting(false);
    }
  }, [incomingChat, accepting, navigate]);

  const declineChat = useCallback(async () => {
    if (!incomingChat) return;
    const chatId = incomingChat.chat_id;
    setDismissed((s) => new Set(s).add(chatId));
    setIncomingChat(null);
    try {
      await api.post(`/astrologer/chats/${chatId}/decline`);
    } catch (_) { /* if backend rejects, the dismissal is still local */ }
  }, [incomingChat]);

  const openCall = useCallback(() => {
    if (!incomingCall) return;
    const callId = incomingCall.call_id;
    setIncomingCall(null);
    navigate(`/astrologer/dashboard?fullscreen_call=${callId}`);
  }, [incomingCall, navigate]);

  const dismissCall = useCallback(() => {
    if (!incomingCall) return;
    setDismissed((s) => new Set(s).add(incomingCall.call_id));
    setIncomingCall(null);
  }, [incomingCall]);

  if (!me || onDashboard || onAuthPage) return null;
  if (!incomingChat && !incomingCall) return null;

  return (
    <div
      className="fixed inset-x-0 top-0 z-[160] flex justify-center pointer-events-none px-3 pt-[max(env(safe-area-inset-top),0.75rem)]"
      data-testid="global-astro-ring"
    >
      <div
        className="pointer-events-auto w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden bg-gradient-to-r from-[#3F2D1B] via-[#4a3624] to-[#3F2D1B] border border-amber-400/50 ring-1 ring-amber-300/30"
        style={{ animation: "ring-slide-down 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2)" }}
      >
        {incomingChat && (
          <div className="px-3 py-3 sm:px-4 sm:py-3.5 flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-11 h-11 rounded-full bg-amber-400/20 flex items-center justify-center border-2 border-amber-300/70 animate-pulse">
                <ChatCircleDots size={22} weight="fill" className="text-amber-300" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-[#3F2D1B] animate-ping-slow" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                Incoming chat
              </div>
              <div className="text-amber-50 font-serif-display text-base sm:text-lg truncate" data-testid="global-ring-chat-name">
                💬 {incomingChat.customer_name}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={declineChat}
                className="rounded-full px-2.5 sm:px-3 py-2 bg-white/10 hover:bg-white/20 text-rose-200 transition flex items-center gap-1 text-xs font-semibold"
                aria-label="Decline chat"
                data-testid="global-ring-chat-decline"
              >
                <XCircle size={16} weight="fill" />
                <span className="hidden sm:inline">Decline</span>
              </button>
              <button
                type="button"
                onClick={acceptChat}
                disabled={accepting}
                className="rounded-full px-3 sm:px-4 py-2 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-[#1A1410] transition flex items-center gap-1.5 text-xs font-bold disabled:opacity-60"
                data-testid="global-ring-chat-accept"
              >
                <CheckCircle size={16} weight="fill" />
                Accept
              </button>
            </div>
          </div>
        )}

        {incomingCall && (
          <div className={`px-3 py-3 sm:px-4 sm:py-3.5 flex items-center gap-3 ${incomingChat ? "border-t border-amber-300/30" : ""}`}>
            <div className="relative flex-shrink-0">
              <div className="w-11 h-11 rounded-full bg-emerald-400/20 flex items-center justify-center border-2 border-emerald-300/70 animate-pulse">
                <Phone size={22} weight="fill" className="text-emerald-300" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#3F2D1B] animate-ping-slow" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
                Incoming call · {incomingCall.mode || "voice"}
              </div>
              <div className="text-amber-50 font-serif-display text-base sm:text-lg truncate" data-testid="global-ring-call-name">
                📞 {incomingCall.seeker_name || "Phone seeker"}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={dismissCall}
                className="rounded-full p-2 bg-white/10 hover:bg-white/20 text-amber-100 transition"
                aria-label="Dismiss"
                data-testid="global-ring-call-dismiss"
              >
                <X size={16} weight="bold" />
              </button>
              <button
                type="button"
                onClick={openCall}
                className="rounded-full px-3 sm:px-4 py-2 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-[#0a1a12] transition flex items-center gap-1.5 text-xs font-bold"
                data-testid="global-ring-call-open"
              >
                <CheckCircle size={16} weight="fill" />
                Open
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes ring-slide-down {
          0% { opacity: 0; transform: translateY(-32px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(1.6); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 1.4s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}
